import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import {
    Plus,
    Search,
    Building2,
    MapPin,
    Phone,
    Mail,
    Edit2,
    Trash2,
    MoreHorizontal,
    Filter,
    SortAsc,
    Users,
    BookOpen,
    Eye,
    TrendingUp,
    Calendar,
    Star,
    Activity,
    AlertCircle,
    X,
} from 'lucide-react';
import { campusApi, classesApi, usersApi } from '../services/api';
import { parseCoordinates } from '../utils/geolocation';
import CampusLocationField from '../components/campus/CampusLocationField';
import PageContainer from '../components/PageContainer';
import {
    ActionMenu,
    Button,
    DataToolbar,
    DetailFooter,
    DetailHeader,
    DetailItem,
    DetailList,
    DetailSection,
    DetailStats,
    EmptyState,
    LocalPagination,
    LoadingState,
    Modal,
    ResponsiveDataView,
    SectionPanel,
    Input,
    Select,
    StatCard,
    StatusBadge,
    Textarea,
    useLocalPagination,
    useToast,
} from '../components/ui';
import { openOnRowClick } from '../utils/rowClick';
import { matchesQuery } from '../utils/search';

const CAMPUSES_PAGE_SIZE = 5;

const Campus = () => {
    const navigate = useNavigate();
    const { hasPermission, hasAnyPermission } = usePermissions();
    const { showToast: showUiToast } = useToast();

    // Check if user has permission to view campus
    const canViewCampus = hasAnyPermission([
        { resource: 'campus', action: 'read_all' },
        { resource: 'campus', action: 'read_own' },
        { resource: 'campus', action: 'read' }
    ]);

    // If user doesn't have permission, show access denied message
    if (!canViewCampus) {
        return (
            <div className="min-h-screen bg-ink-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="bg-danger-50 rounded-full p-6 mb-6 mx-auto w-fit">
                        <svg className="w-16 h-16 text-danger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-ink-900 mb-2">Bạn không thể xem trang này</h2>
                    <p className="text-ink-600 mb-8">
                        Bạn không có quyền truy cập vào trang Cơ sở. Vui lòng liên hệ quản trị viên để được cấp quyền.
                    </p>
                    <div className="flex space-x-4 justify-center">
                        <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-3 bg-ink-100 text-ink-700 rounded-lg hover:bg-ink-200 transition-colors duration-200"
                        >
                            Quay lại
                        </button>
                        <button
                            onClick={() => navigate('/attendance')}
                            className="px-6 py-3 bg-academy-600 text-white rounded-lg hover:bg-academy-700 transition-colors duration-200 text-sm font-medium"
                        >
                            Đi tới Điểm danh
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showFilters, setShowFilters] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | number | null>(null);

    // New states for modals
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCampus, setSelectedCampus] = useState<any>(null);
    const [campusToDelete, setCampusToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        manager: '',
        managerIds: [] as string[],
        address: '',
        phone: '',
        email: '',
        capacity: '',
        status: 'true',
        description: '',
        coordinates: '',
        checkInRadiusM: '150'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    const [campuses, setCampuses] = useState<any[]>([]);
    const [classes, setClasses] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const managerOptions = users.filter(user => user.role === 'MANAGER' || user.role === 'ADMIN');

    const getCampusManagerIds = (campus: any) => {
        if (Array.isArray(campus.managerIds) && campus.managerIds.length > 0) {
            return campus.managerIds;
        }
        if (Array.isArray(campus.managerInfos) && campus.managerInfos.length > 0) {
            return campus.managerInfos.map((manager: any) => manager.id).filter(Boolean);
        }
        if (campus.managerInfo?.id) {
            return [campus.managerInfo.id];
        }
        return [];
    };

    const getCampusManagerDisplay = (campus: any) => {
        if (Array.isArray(campus.managerInfos) && campus.managerInfos.length > 0) {
            return campus.managerInfos
                .map((manager: any) => `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.email)
                .filter(Boolean)
                .join(', ');
        }
        return campus.manager || 'Chưa có';
    };

    const resetFormData = () => {
        setFormData({
            name: '',
            manager: '',
            managerIds: [],
            address: '',
            phone: '',
            email: '',
            capacity: '',
            status: 'true',
            description: '',
            coordinates: '',
            checkInRadiusM: '150'
        });
    };

    const handleManagerToggle = (managerId: string) => {
        setFormData(prev => {
            const selected = prev.managerIds.includes(managerId)
                ? prev.managerIds.filter(id => id !== managerId)
                : [...prev.managerIds, managerId];

            return {
                ...prev,
                managerIds: selected,
                manager: selected[0] || ''
            };
        });

        if (formErrors.manager) {
            setFormErrors(prev => ({ ...prev, manager: '' }));
        }
    };

    const renderManagerSelector = () => (
        <div className="space-y-1.5">
            <p className="text-sm font-medium text-ink-700">Quản lý cơ sở *</p>
            {managerOptions.length > 0 ? (
                <div className="grid max-h-52 gap-2 overflow-y-auto sm:grid-cols-2">
                    {managerOptions.map(user => {
                        const checked = formData.managerIds.includes(user.id);
                        return (
                            <label
                                key={user.id}
                                className={`flex min-w-0 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors duration-200 ${checked
                                    ? 'border-academy-300 bg-academy-50'
                                    : formErrors.manager ? 'border-danger-100 bg-white hover:bg-ink-50' : 'border-ink-200 bg-white hover:bg-ink-50'
                                    }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => handleManagerToggle(user.id)}
                                    className="h-4 w-4 shrink-0 rounded border-ink-200 text-academy-600 focus:ring-academy-200"
                                />
                                <span className="min-w-0">
                                    <span className="block truncate text-sm font-medium text-ink-900">
                                        {user.firstName} {user.lastName}
                                    </span>
                                    <span className="block truncate text-xs text-ink-500">{user.email}</span>
                                </span>
                            </label>
                        );
                    })}
                </div>
            ) : (
                <p className="rounded-lg border border-dashed border-ink-200 px-3 py-2 text-sm text-ink-500">Chưa có quản lý</p>
            )}
            {formErrors.manager && (
                <p className="text-sm text-danger-600">{formErrors.manager}</p>
            )}
        </div>
    );

    // Function to load all data
    const loadData = async () => {
        try {
            const [campusesRes, classesRes, managersRes] = await Promise.all([
                campusApi.getAll(),
                classesApi.getAll(),
                usersApi.getManagers()
            ]);
            setCampuses(campusesRes.data.data || []);
            setClasses(classesRes.data.data || []);
            setUsers(managersRes.data.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        loadData();
    }, []);

    // Calculate statistics for each campus
    const getCampusStats = (campusId: string) => {
        // Count classes for this campus
        const campusClasses = classes.filter(cls =>
            (typeof cls.campus === 'object' ? cls.campus?.id : cls.campus) === campusId ||
            cls.campusId === campusId
        );

        // Count students (enrolled in classes of this campus)
        const totalStudents = campusClasses.reduce((sum, cls) => sum + (cls.enrolled || 0), 0);

        // Count teachers working in this campus
        const teacherIds = new Set();
        campusClasses.forEach(cls => {
            if (cls.teacherInfo?.id) teacherIds.add(cls.teacherInfo.id);
            if (cls.assistantInfo?.id) teacherIds.add(cls.assistantInfo.id);
        });

        return {
            students: totalStudents,
            classes: campusClasses.length,
            teachers: teacherIds.size
        };
    };

    const handleCreateCampus = async (formData: any): Promise<boolean> => {
        try {
            // console.log('Calling campusApi.create with:', formData);
            const res = await campusApi.create(formData);
            // console.log('campusApi.create response:', res);
            setCampuses(prev => [...prev, res.data.data]);
            return true;
        } catch (e: any) {
            console.error('Error in handleCreateCampus:', e);
            console.error('Error response:', e?.response?.data);
            showUiToast(e?.response?.data?.message || 'Cơ sở đã tồn tại.', 'error');
            return false;
        }
    };

    // Filter and sort campuses
    const filteredAndSortedCampuses = campuses
        .filter(campus => {
            const matchesSearch = matchesQuery([campus.name, campus.address, getCampusManagerDisplay(campus)].filter(Boolean).join(' '), searchTerm);
            const matchesStatus = selectedStatus === 'all' ||
                (selectedStatus === 'active' && campus.isActive) ||
                (selectedStatus === 'inactive' && !campus.isActive);
            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            let aValue = a[sortBy as keyof typeof a];
            let bValue = b[sortBy as keyof typeof b];

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
            }

            if (sortOrder === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    const {
        page: campusesPage,
        pageSize: campusesPageSize,
        pagedItems: pagedCampuses,
        setPage: setCampusesPage,
        setPageSize: setCampusesPageSize,
    } = useLocalPagination(
        filteredAndSortedCampuses,
        CAMPUSES_PAGE_SIZE,
        `${searchTerm}|${selectedStatus}|${sortBy}|${sortOrder}`,
    );

    const stats = {
        total: campuses.length,
        active: campuses.filter(c => c.isActive).length,
        totalClasses: classes.length,
        totalStudents: classes.reduce((sum, c) => sum + (c.enrolledStudents || c.enrolled || 0), 0),
        // totalTeachers: campuses.reduce((sum, c) => sum + (c.teachers || 0), 0),
        // totalRevenue: campuses.reduce((sum, c) => sum + (c.revenue || 0), 0)
    };

    const formatCurrency = (amount: number) => {
        const safeAmount = Number.isFinite(amount) ? amount : 0;
        return (safeAmount / 1_000_000).toFixed(1) + 'M₫';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('vi-VN');
    };

    const handleDropdownToggle = (campusId: string | number) => {
        setActiveDropdown(activeDropdown === campusId ? null : campusId);
    };

    const handleDropdownAction = (action: string, campus: any) => {
        setActiveDropdown(null); // Close dropdown first
        setSelectedCampus(campus);

        switch (action) {
            case 'view':
                setShowDetailModal(true);
                break;
            case 'edit':
                // Pre-fill form with campus data
                const managerIds = getCampusManagerIds(campus);
                setFormData({
                    name: campus.name || '',
                    manager: managerIds[0] || '',
                    managerIds,
                    address: campus.address || '',
                    phone: campus.phone || '',
                    email: campus.email || '',
                    capacity: String(campus.capacity ?? campus.students ?? ''),
                    status: String(campus.isActive),
                    description: campus.description || '',
                    coordinates: campus.latitude != null && campus.longitude != null
                        ? `${campus.latitude}, ${campus.longitude}`
                        : '',
                    checkInRadiusM: String(campus.checkInRadiusM ?? 150)
                });
                setShowEditModal(true);
                break;
            case 'delete':
                setCampusToDelete(campus);
                setShowDeleteModal(true);
                break;
            default:
                break;
        }
    };

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    // Form handling
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const locationPayload = () => {
        const coordinates = parseCoordinates(formData.coordinates);
        return {
            latitude: coordinates?.latitude ?? null,
            longitude: coordinates?.longitude ?? null,
            checkInRadiusM: Number(formData.checkInRadiusM),
        };
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.name.trim()) {
            errors.name = 'Tên cơ sở là bắt buộc';
        }

        if (formData.managerIds.length === 0) {
            errors.manager = 'Chọn ít nhất một quản lý';
        }

        if (!formData.address.trim()) {
            errors.address = 'Địa chỉ là bắt buộc';
        }

        if (!formData.phone.trim()) {
            errors.phone = 'Số điện thoại là bắt buộc';
        } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
            errors.phone = 'Số điện thoại không hợp lệ';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email là bắt buộc';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Email không hợp lệ';
        }

        // Capacity validation removed as it's not required for campus creation

        if (formData.coordinates.trim() && !parseCoordinates(formData.coordinates)) {
            errors.coordinates = 'Tọa độ không hợp lệ. Dán theo dạng "10.7769, 106.7009" từ Google Maps';
        }

        const radius = Number(formData.checkInRadiusM);
        if (!Number.isInteger(radius) || radius < 20 || radius > 5000) {
            errors.checkInRadiusM = 'Bán kính từ 20 đến 5000 mét';
        }

        setFormErrors(errors);
        // console.log('Validation errors:', errors);
        // console.log('selectedCampus:', selectedCampus);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // console.log('handleSubmit called with formData:', formData);

        if (!validateForm()) {
            // console.log('Form validation failed');
            return;
        }

        // console.log('Form validation passed, starting submission');
        setIsSubmitting(true);

        try {
            const campusData = {
                name: formData.name,
                address: formData.address,
                phone: formData.phone,
                email: formData.email,
                isActive: formData.status === 'true',
                managerId: formData.managerIds[0],
                managerIds: formData.managerIds,
                description: formData.description,
                ...locationPayload(),
            };
            // console.log('Sending campus data:', campusData);

            const created = await handleCreateCampus(campusData);
            // console.log('handleCreateCampus result:', created);

            if (!created) {
                // console.log('Campus creation failed');
                setIsSubmitting(false);
                return;
            }

            // Reload all data to get updated campus list
            await loadData();

            // Reset form and close modal
            resetFormData();
            setShowForm(false);

            // Show success message (you can implement a toast notification here)
            showToast('Cơ sở mới đã được tạo thành công!', 'success');

        } catch (error) {
            console.error('Error creating campus:', error);
            showUiToast('Có lỗi xảy ra khi tạo cơ sở. Vui lòng thử lại.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        resetFormData();
        setFormErrors({});
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await campusApi.update(String(selectedCampus.id), {
                name: formData.name,
                address: formData.address,
                phone: formData.phone,
                email: formData.email,
                managerId: formData.managerIds[0],
                managerIds: formData.managerIds,
                isActive: formData.status === 'true',
                description: formData.description,
                ...locationPayload(),
            });

            // Reload all data to get updated campus with manager info
            await loadData();

            setShowEditModal(false);
            setSelectedCampus(null);

            // Show success message
            showToast('Cơ sở đã được cập nhật thành công!', 'success');

        } catch (error) {
            console.error('Error updating campus:', error);
            showToast('Có lỗi xảy ra khi cập nhật cơ sở. Vui lòng thử lại.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedCampus(null);
        resetFormData();
        setFormErrors({});
    };

    const handleConfirmDelete = async () => {
        if (!campusToDelete) return;

        setIsDeleting(true);
        try {
            await campusApi.delete(campusToDelete.id);
            setCampuses(prev => prev.filter(c => c.id !== campusToDelete.id));
            showToast(`✅ Đã xóa cơ sở "${campusToDelete.name}" thành công!`, 'success');

        } catch (error) {
            console.error('Error deleting campus:', error);
            showToast('❌ Không thể xóa cơ sở. Vui lòng thử lại!', 'error');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setCampusToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setCampusToDelete(null);
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        showUiToast(message, type);
    };

    const setFormField = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const formSection = (title: string, description: string | null, children: React.ReactNode) => (
        <section className="space-y-4 border-t border-ink-200 pt-5 first:border-t-0 first:pt-0">
            <div>
                <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
                {description ? <p className="mt-0.5 text-xs text-ink-500">{description}</p> : null}
            </div>
            {children}
        </section>
    );

    const renderCampusFormFields = () => (
        <div className="space-y-5">
            {formSection('Thông tin cơ sở', null, (
                <>
                    <Input
                        label="Tên cơ sở *"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="VD: Cơ sở Quận 1"
                        error={formErrors.name}
                    />
                    <Input
                        label="Địa chỉ *"
                        name="address"
                        value={formData.address}
                        onChange={handleFormChange}
                        placeholder="Số nhà, đường, phường, quận/huyện"
                        error={formErrors.address}
                    />
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Input
                            label="Số điện thoại *"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleFormChange}
                            placeholder="028-1234-5678"
                            error={formErrors.phone}
                        />
                        <Input
                            label="Email *"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            placeholder="coso@athena.edu.vn"
                            error={formErrors.email}
                        />
                    </div>
                    <Textarea
                        label="Mô tả"
                        name="description"
                        value={formData.description}
                        onChange={handleFormChange}
                        placeholder="Ghi chú thêm về cơ sở (không bắt buộc)"
                        rows={2}
                    />
                </>
            ))}

            {formSection(
                'Vị trí điểm danh',
                'Giáo viên/trợ giảng điểm danh trong bán kính này quanh tọa độ được tính là có mặt tại cơ sở.',
                <CampusLocationField
                    coordinates={formData.coordinates}
                    radius={formData.checkInRadiusM}
                    coordinatesError={formErrors.coordinates}
                    radiusError={formErrors.checkInRadiusM}
                    onCoordinatesChange={value => setFormField('coordinates', value)}
                    onRadiusChange={value => setFormField('checkInRadiusM', value)}
                />,
            )}

            {formSection('Quản lý & trạng thái', null, (
                <>
                    {renderManagerSelector()}
                    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-ink-200 bg-white px-3 py-2.5">
                        <span>
                            <span className="block text-sm font-medium text-ink-900">Đang hoạt động</span>
                            <span className="block text-xs text-ink-500">Tắt khi cơ sở tạm ngừng; dữ liệu cũ vẫn được giữ.</span>
                        </span>
                        <span className="relative inline-flex shrink-0 items-center">
                            <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={formData.status === 'true'}
                                onChange={event => setFormField('status', event.target.checked ? 'true' : 'false')}
                            />
                            <span className="h-6 w-11 rounded-full bg-ink-300 transition-colors peer-checked:bg-academy-600 peer-focus-visible:ring-2 peer-focus-visible:ring-academy-200" />
                            <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                        </span>
                    </label>
                </>
            ))}
        </div>
    );

    const renderCampusCustomMenu = (campus: any) => (
        <ActionMenu
            open={activeDropdown === campus.id}
            onOpenChange={(open) => setActiveDropdown(open ? campus.id : null)}
            triggerLabel={`Tuỳ chỉnh cơ sở ${campus.name || 'chưa có tên'}`}
            triggerTitle="Tuỳ chỉnh"
            triggerIcon={<MoreHorizontal className="h-4 w-4" />}
            width={192}
        >
            <button
                type="button"
                role="menuitem"
                onClick={() => handleDropdownAction('edit', campus)}
                className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-50"
            >
                <Edit2 className="mr-3 h-4 w-4" />
                Chỉnh sửa
            </button>
            <div className="my-1 border-t border-ink-100" />
            <button
                type="button"
                role="menuitem"
                onClick={() => handleDropdownAction('delete', campus)}
                className="flex w-full items-center px-4 py-2 text-sm text-danger-600 transition-colors hover:bg-danger-50"
            >
                <Trash2 className="mr-3 h-4 w-4" />
                Xóa cơ sở
            </button>
        </ActionMenu>
    );

    const renderCampusStatus = (campus: any) => (
        <StatusBadge tone={campus.isActive ? 'success' : 'neutral'}>
            {campus.isActive ? 'Hoạt động' : 'Ngừng hoạt động'}
        </StatusBadge>
    );

    const renderCampusTable = () => (
        <div className="min-w-[920px]">
            <table className="min-w-full divide-y divide-ink-200">
                <thead>
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Cơ sở</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Liên hệ</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Quản lý</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Trạng thái</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Thống kê</th>
                        <th className="w-12 px-3 py-3"><span className="sr-only">Tuỳ chỉnh</span></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-ink-200 bg-white">
                    {pagedCampuses.map((campus) => (
                        <tr key={campus.id} onClick={openOnRowClick(() => handleDropdownAction('view', campus))} className="cursor-pointer transition-colors hover:bg-ink-50">
                            <td className="px-6 py-4">
                                <div className="flex items-center">
                                    <div className="min-w-0">
                                        <button
                                            type="button"
                                            onClick={() => handleDropdownAction('view', campus)}
                                            aria-label={`Xem chi tiết cơ sở ${campus.name || 'chưa có tên'}`}
                                            className="cursor-pointer truncate text-left text-sm font-medium text-ink-900 hover:text-academy-600"
                                        >
                                            {campus.name}
                                        </button>
                                        <div className="flex items-center truncate text-sm text-ink-500">
                                            <MapPin className="mr-1 h-3 w-3 shrink-0" />
                                            <span className="truncate">{campus.address}</span>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">
                                <div className="text-sm text-ink-900">{campus.phone}</div>
                                <div className="text-sm text-ink-500">{campus.email}</div>
                            </td>
                            <td className="px-6 py-4 text-sm font-medium text-ink-900">
                                {getCampusManagerDisplay(campus)}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4">{renderCampusStatus(campus)}</td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-ink-800">
                                <div className="space-y-1">
                                    <div className="flex items-center">
                                        <Users className="mr-1 h-3 w-3 text-academy-500" />
                                        <span>{getCampusStats(campus.id).students} học sinh</span>
                                    </div>
                                    <div className="flex items-center">
                                        <BookOpen className="mr-1 h-3 w-3 text-action-600" />
                                        <span>{getCampusStats(campus.id).classes} lớp</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Users className="mr-1 h-3 w-3 text-warning-600" />
                                        <span>{getCampusStats(campus.id).teachers} giáo viên</span>
                                    </div>
                                </div>
                            </td>
                            <td className="px-3 py-4 text-center">{renderCampusCustomMenu(campus)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderCampusCards = () => (
        <div className="grid grid-cols-1 gap-3">
            {pagedCampuses.map((campus) => (
                <article key={campus.id} onClick={openOnRowClick(() => handleDropdownAction('view', campus))} className="min-w-0 cursor-pointer rounded-lg border border-ink-200 bg-white p-4 transition-colors hover:bg-ink-50">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <div className="min-w-0">
                                <h3 className="truncate text-base font-semibold text-ink-900">
                                    <button
                                        type="button"
                                        onClick={() => handleDropdownAction('view', campus)}
                                        aria-label={`Xem chi tiết cơ sở ${campus.name || 'chưa có tên'}`}
                                        className="max-w-full cursor-pointer truncate text-left"
                                    >
                                        {campus.name}
                                    </button>
                                </h3>
                                <p className="mt-1 truncate text-sm text-ink-500">{getCampusManagerDisplay(campus)}</p>
                                <div className="mt-2">{renderCampusStatus(campus)}</div>
                            </div>
                        </div>
                        <div className="flex justify-end">
                            {renderCampusCustomMenu(campus)}
                        </div>
                    </div>

                    <div className="mt-3 space-y-1 text-sm text-ink-600">
                        <p className="flex min-w-0 items-center gap-2">
                            <MapPin className="h-4 w-4 shrink-0 text-ink-400" />
                            <span className="truncate">{campus.address}</span>
                        </p>
                        <p className="flex min-w-0 items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0 text-ink-400" />
                            <span className="truncate">{campus.phone || 'Chưa có số điện thoại'}</span>
                        </p>
                        <p className="flex min-w-0 items-center gap-2">
                            <Mail className="h-4 w-4 shrink-0 text-ink-400" />
                            <span className="truncate">{campus.email || 'Chưa có email'}</span>
                        </p>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                        <div>
                            <p className="text-base font-semibold text-academy-700">{getCampusStats(campus.id).students}</p>
                            <p className="text-[11px] text-ink-500">Học sinh</p>
                        </div>
                        <div>
                            <p className="text-base font-semibold text-action-700">{getCampusStats(campus.id).classes}</p>
                            <p className="text-[11px] text-ink-500">Lớp</p>
                        </div>
                        <div>
                            <p className="text-base font-semibold text-warning-700">{getCampusStats(campus.id).teachers}</p>
                            <p className="text-[11px] text-ink-500">Giáo viên</p>
                        </div>
                    </div>

                    <p className="mt-3 truncate text-xs text-ink-500">Tạo: {formatDate(campus.createdAt)}</p>
                </article>
            ))}
        </div>
    );

    return (
        <PageContainer
            title="Cơ sở"
            subtitle={`${campuses.length} cơ sở · liên hệ, người quản lý và quy mô từng nơi.`}
            actions={hasPermission('campus', 'create') ? (
                <Button
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                        resetFormData();
                        setFormErrors({});
                        setShowForm(true);
                    }}
                >
                    Thêm cơ sở
                </Button>
            ) : null}
        >

            {/* Stats Cards */}
            <div className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-1 sm:grid-cols-3">
                <StatCard
                    label="Tổng cơ sở"
                    value={stats.total}
                    detail="Đang quản lý"
                    icon={<Building2 className="h-5 w-5" />}
                    tone="academy"
                />
                <StatCard
                    label="Đang hoạt động"
                    value={stats.active}
                    detail={`${stats.total > 0 ? ((stats.active / stats.total) * 100).toFixed(1) : 0}%`}
                    icon={<Activity className="h-5 w-5" />}
                    tone="action"
                />
                <StatCard
                    label="Tổng học sinh"
                    value={stats.totalStudents}
                    detail={`${stats.totalClasses} lớp học`}
                    icon={<Users className="h-5 w-5" />}
                    tone="academy"
                />

                {/* <div className="bg-white rounded-xl shadow-sm border border-ink-100 p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-ink-600">Doanh thu/Tháng</p>
              <p className="text-2xl font-bold text-warning-600">{formatCurrency(stats.totalRevenue)}</p>
              <div className="flex items-center mt-1">
                <TrendingUp className="w-4 h-4 text-warning-500 mr-1" />
                <span className="text-sm text-warning-600">+8% so với tháng trước</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-warning-500 rounded-lg flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
          </div>
        </div> */}
            </div>

            {/* Filters and Search */}
            <DataToolbar
                className="sm:items-center"
                controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-3 lg:flex lg:flex-wrap lg:items-center"
            >
                <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tìm cơ sở, địa chỉ, quản lý…"
                    leftIcon={<Search className="h-4 w-4" />}
                    aria-label="Tìm kiếm cơ sở"
                    className="min-h-9 lg:w-72"
                />
                <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    aria-label="Lọc theo trạng thái"
                    className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                >
                    <option value="all">Mọi trạng thái</option>
                    <option value="active">Đang hoạt động</option>
                    <option value="inactive">Ngừng hoạt động</option>
                </select>
                <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                        const [field, order] = e.target.value.split('-');
                        setSortBy(field);
                        setSortOrder(order);
                    }}
                    aria-label="Sắp xếp"
                    className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                >
                    <option value="name-asc">Tên A-Z</option>
                    <option value="name-desc">Tên Z-A</option>
                    <option value="students-desc">Nhiều học sinh nhất</option>
                    <option value="students-asc">Ít học sinh nhất</option>
                    <option value="createdAt-desc">Mới nhất</option>
                    <option value="createdAt-asc">Cũ nhất</option>
                    <option value="rating-desc">Đánh giá cao nhất</option>
                </select>

                {showFilters && (
                    <div className="mt-4 p-4 bg-ink-50 rounded-lg border border-ink-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">Số lượng học sinh</label>
                                <select className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100">
                                    <option value="">Tất cả</option>
                                    <option value="0-50">0-50 học sinh</option>
                                    <option value="51-100">51-100 học sinh</option>
                                    <option value="101-200">101-200 học sinh</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">Đánh giá</label>
                                <select className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100">
                                    <option value="">Tất cả</option>
                                    <option value="4.5+">4.5★ trở lên</option>
                                    <option value="4.0+">4.0★ trở lên</option>
                                    <option value="3.5+">3.5★ trở lên</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink-700 mb-1">Năm thành lập</label>
                                <select className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100">
                                    <option value="">Tất cả</option>
                                    <option value="2024">2024</option>
                                    <option value="2023">2023</option>
                                    <option value="2022">2022 trở về trước</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </DataToolbar>

            {loading ? (
                <SectionPanel><LoadingState title="Đang tải danh sách cơ sở..." /></SectionPanel>
            ) : filteredAndSortedCampuses.length === 0 ? (
                <SectionPanel>
                    <EmptyState
                        title="Không tìm thấy cơ sở"
                        description="Thử thay đổi bộ lọc hoặc tạo cơ sở mới."
                    />
                    {hasPermission('campus', 'create') && (
                        <div className="pb-6 text-center">
                        <Button
                            onClick={() => {
                                // Reset form data when opening create modal
                                resetFormData();
                                setFormErrors({});
                                setShowForm(true);
                            }}
                        >
                            Thêm cơ sở mới
                        </Button>
                        </div>
                    )}
                </SectionPanel>
            ) : (
                <SectionPanel className="overflow-hidden p-0 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">
                    <ResponsiveDataView
                        desktopLabel="Danh sách cơ sở"
                        mobileLabel="Thẻ cơ sở"
                        desktop={renderCampusTable()}
                        mobile={renderCampusCards()}
                    />
                    <LocalPagination
                        label="danh sách cơ sở"
                        page={campusesPage}
                        pageSize={campusesPageSize}
                        totalItems={filteredAndSortedCampuses.length}
                        pageSizeOptions={[5, 10, 20]}
                        onPageChange={setCampusesPage}
                        onPageSizeChange={setCampusesPageSize}
                    />
                </SectionPanel>
            )}

            <Modal
                open={showForm}
                title="Thêm cơ sở mới"
                onClose={handleCloseForm}
                className="sm:max-w-2xl"
                footer={(
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={handleCloseForm} disabled={isSubmitting}>
                            Hủy
                        </Button>
                        <Button type="submit" form="campus-create-form" loading={isSubmitting}>
                            {isSubmitting ? 'Đang tạo...' : 'Tạo cơ sở'}
                        </Button>
                    </div>
                )}
            >
                <form id="campus-create-form" onSubmit={handleSubmit}>
                    {renderCampusFormFields()}
                </form>
            </Modal>

            {selectedCampus && (
                <Modal
                    open={showDetailModal}
                    title="Chi tiết cơ sở"
                    onClose={() => setShowDetailModal(false)}
                    className="lg:max-w-3xl"
                    footer={(
                        <DetailFooter
                            onClose={() => setShowDetailModal(false)}
                            onEdit={hasPermission('campus', 'update') ? () => {
                                setShowDetailModal(false);
                                setTimeout(() => {
                                    handleDropdownAction('edit', selectedCampus);
                                }, 100);
                            } : undefined}
                        />
                    )}
                >
                    <div className="space-y-4">
                        <DetailHeader
                            icon={<Building2 className="h-6 w-6" />}
                            title={selectedCampus.name}
                            subtitle={`Quản lý: ${getCampusManagerDisplay(selectedCampus)}`}
                            badge={renderCampusStatus(selectedCampus)}
                        />
                        <DetailStats items={[
                            { label: 'Học sinh', value: getCampusStats(selectedCampus.id).students },
                            { label: 'Lớp học', value: getCampusStats(selectedCampus.id).classes },
                            { label: 'Giáo viên', value: getCampusStats(selectedCampus.id).teachers },
                        ]} />
                        <div className="grid gap-4 md:grid-cols-2">
                            <DetailSection title="Liên hệ">
                                <DetailList>
                                    <DetailItem icon={<MapPin />} label="Địa chỉ" value={selectedCampus.address} />
                                    <DetailItem icon={<Phone />} label="Điện thoại" value={selectedCampus.phone} />
                                    <DetailItem icon={<Mail />} label="Email" value={selectedCampus.email} />
                                </DetailList>
                            </DetailSection>
                            <DetailSection title="Vận hành">
                                <DetailList>
                                    <DetailItem icon={<Users />} label="Quản lý" value={getCampusManagerDisplay(selectedCampus)} />
                                    <DetailItem
                                        icon={<MapPin />}
                                        label="Tọa độ điểm danh"
                                        value={selectedCampus.latitude != null && selectedCampus.longitude != null
                                            ? `${selectedCampus.latitude}, ${selectedCampus.longitude} · bán kính ${selectedCampus.checkInRadiusM ?? 150}m`
                                            : ''}
                                        fallback="Chưa cài đặt"
                                    />
                                    <DetailItem icon={<Calendar />} label="Ngày tạo" value={formatDate(selectedCampus.createdAt)} />
                                </DetailList>
                            </DetailSection>
                        </div>
                    </div>
                </Modal>
            )}

            {selectedCampus && (
                <Modal
                    open={showEditModal}
                    title="Chỉnh sửa cơ sở"
                    description={selectedCampus.name}
                    onClose={handleCloseEditModal}
                    className="sm:max-w-2xl"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={handleCloseEditModal} disabled={isSubmitting}>
                                Hủy
                            </Button>
                            <Button type="submit" form="campus-edit-form" loading={isSubmitting}>
                                {isSubmitting ? 'Đang lưu...' : 'Lưu thay đổi'}
                            </Button>
                        </div>
                    )}
                >
                    <form id="campus-edit-form" onSubmit={handleEditSubmit}>
                        {renderCampusFormFields()}
                    </form>
                </Modal>
            )}

            {campusToDelete && (
                <Modal
                    open={showDeleteModal}
                    title="Xác nhận xóa cơ sở"
                    description="Hành động này không thể hoàn tác"
                    onClose={handleCancelDelete}
                    className="sm:max-w-md"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={handleCancelDelete} disabled={isDeleting}>
                                Hủy
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleConfirmDelete}
                                loading={isDeleting}
                                icon={<Trash2 className="h-4 w-4" />}
                            >
                                {isDeleting ? 'Đang xóa...' : 'Xác nhận xóa'}
                            </Button>
                        </div>
                    )}
                >
                    <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-danger-100 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-6 h-6 text-danger-600" />
                            </div>
                            <div className="flex-1">
                                <h4 className="font-medium text-ink-900">Bạn có chắc chắn muốn xóa cơ sở này?</h4>
                                <p className="text-sm text-ink-600 mt-1">Hành động này không thể hoàn tác!</p>
                            </div>
                        </div>

                        <div className="bg-ink-50 rounded-lg p-4">
                            <h5 className="font-medium text-ink-900 mb-2">Thông tin cơ sở sẽ bị xóa:</h5>
                            <div className="space-y-1 text-sm text-ink-600">
                                <p><span className="font-medium">Tên cơ sở:</span> {campusToDelete.name}</p>
                                <p><span className="font-medium">Địa chỉ:</span> {campusToDelete.address}</p>
                                <p><span className="font-medium">Quản lý:</span> {getCampusManagerDisplay(campusToDelete)}</p>
                            </div>
                        </div>

                        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                            <div className="flex items-start space-x-2">
                                <AlertCircle className="w-5 h-5 text-danger-600 mt-0.5" />
                                <div className="text-sm text-danger-800">
                                    <p className="font-medium">Lưu ý quan trọng:</p>
                                    <ul className="mt-1 space-y-1">
                                        <li>• Tất cả lớp học thuộc cơ sở này sẽ bị xóa</li>
                                        <li>• Dữ liệu học sinh và giáo viên sẽ bị ảnh hưởng</li>
                                        <li>• Không thể khôi phục sau khi xóa</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Click outside to close dropdown */}
            {activeDropdown && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setActiveDropdown(null)}
                />
            )}
        </PageContainer>
    );
};

export default Campus;
